<template>
  <div>
    <a-tabs @change="handleTabChange">
      <a-tab-pane v-for="type in types" :key="type" :tab="$t(`compute.monitor.${type}`)">
        <base-monitor
          v-if="type === 'basic'"
          :data="server"
          :constants="monitorConstants"
          idKey="vm_id"
          monitorType="basic"
          :currentMonitorType="currentMonitorType" />
        <agent-gpu-monitor
          v-else-if="type === 'gpu'"
          :data="server"
          idKey="vm_id" />
        <div v-else>
          <install-agent-form-visible
            :data="server"
            :serverColumns="serverColumns"
            :isPageDestroyed="isPageDestroyed" />
          <agent-monitor
            :data="server"
            :constants="agentMonitorConstants"
            :currentMonitorType="currentMonitorType"
            idKey="vm_id"
            monitorType="agent"
            key="monitor-agent" />
        </div>
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
import _ from 'lodash'
import BaseMonitor from '@Compute/sections/monitor/BaseMonitor'
import AgentMonitor from '@Compute/sections/monitor/AgentMonitor.vue'
import AgentGpuMonitor from '@Compute/sections/monitor/AgentGpuMonitor.vue'
import { ONECLOUD_MONITOR, VMWARE_MONITOR, OTHER_MONITOR, SANGFOR_MONITOR, AGENT_MONITOR } from '@Compute/views/vminstance/constants'
import {
  MONITOR_TEMPLATE_VMINSTANCE,
  resolveMonitorTemplatePanels,
} from '@Monitor/utils/monitorTemplate'
import { HYPERVISORS_MAP } from '@/constants'
import WindowsMixin from '@/mixins/windows'
import InstallAgentFormVisible from '../../vminstance/components/InstallAgentFormVisible'

export default {
  name: 'VminstanceMonitorSidepage',
  components: {
    BaseMonitor,
    AgentMonitor,
    AgentGpuMonitor,
    InstallAgentFormVisible,
  },
  mixins: [WindowsMixin],
  props: {
    data: { // listItemData
      type: Object,
      required: true,
    },
    serverColumns: {
      type: Array,
      required: true,
    },
    isPageDestroyed: Boolean,
    needFetchResource: {
      type: Boolean,
      default: false,
    },
    agentStatus: String,
  },
  data () {
    return {
      currentMonitorType: this.data.hypervisor === 'kvm' ? 'agent' : 'basic',
      alertType: 'warning',
      time: '1h',
      timeGroup: '1m',
      monitorList: [],
      server: this.data,
      gpuHasDevices: false,
    }
  },
  computed: {
    hypervisor () {
      return this.server.hypervisor
    },
    isAgentInstalled () {
      const data = this.server || {}
      let ok = _.get(data, ['metadata', 'sys:monitor_agent']) || _.get(data, ['metadata', '__monitor_agent'])
      const deploy = _.get(data, ['metadata', 'telegraf_deployed'])
      if (Object.prototype.hasOwnProperty.call(data, 'agent_status') || deploy) {
        ok = data.agent_status === 'succeed' || !!deploy
      }
      return !!ok
    },
    hasPassthroughDevices () {
      if (Array.isArray(this.server.isolated_devices) && this.server.isolated_devices.length) return true
      if (Number(this.server.gpu_count) > 0) return true
      return this.gpuHasDevices
    },
    showGpuTab () {
      return this.isAgentInstalled && this.hasPassthroughDevices
    },
    types () {
      const base = this.hypervisor === HYPERVISORS_MAP.kvm.key ? ['agent', 'basic'] : ['basic', 'agent']
      return this.showGpuTab ? [...base, 'gpu'] : base
    },
    // 策略定义中配置的虚拟机监控模板，对全部虚拟机生效
    templateMonitorConstants () {
      return resolveMonitorTemplatePanels(this.$store.getters.scopedPolicy, {
        category: MONITOR_TEMPLATE_VMINSTANCE,
        monitorType: 'basic',
      })
    },
    templateAgentMonitorConstants () {
      return resolveMonitorTemplatePanels(this.$store.getters.scopedPolicy, {
        category: MONITOR_TEMPLATE_VMINSTANCE,
        monitorType: 'agent',
      })
    },
    agentMonitorConstants () {
      return this.templateAgentMonitorConstants.length ? this.templateAgentMonitorConstants : AGENT_MONITOR
    },
    monitorConstants () {
      if (this.templateMonitorConstants.length) {
        return this.templateMonitorConstants
      }
      if (this.hypervisor === HYPERVISORS_MAP.esxi.key) {
        return VMWARE_MONITOR
      } else if (this.hypervisor === HYPERVISORS_MAP.kvm.key) {
        return ONECLOUD_MONITOR
      } else if (this.hypervisor === HYPERVISORS_MAP.sangfor.key) {
        return SANGFOR_MONITOR
      } else {
        // aliyun apsara 虚拟机磁盘使用率增加groupBy: device
        const otherMonitor = OTHER_MONITOR.map(item => {
          if (['Aliyun', 'Apsara'].includes(this.server.brand) && item.fromItem === 'vm_disk') {
            item.groupBy = ['device']
          }
          // azure windows 虚拟机磁盘使用率增加groupBy: device
          if (['Azure'].includes(this.server.brand) && item.fromItem === 'vm_disk' && this.server.os_type === 'Windows') {
            item.groupBy = ['device']
          }
          return item
        })
        return otherMonitor.filter(item => {
          if (!['Aliyun', 'Qcloud'].includes(this.server.brand) && item.seleteItem === 'out_bandwidth_usage') {
            return false
          }
          return true
        })
      }
    },
    serverId () {
      return this.server.id
    },
  },
  watch: {
    data: {
      handler (val) {
        if (!this.needFetchResource && val) {
          this.server = val
        }
      },
      deep: true,
    },
    'data.id' () {
      this.fetchResource()
    },
    serverId: {
      handler (val) {
        if (val) this.fetchGpuDevices()
      },
      immediate: true,
    },
  },
  created () {
    this.fetchResource()
    this.fetchMonitorTemplates()
  },
  methods: {
    fetchMonitorTemplates () {
      // 监控模板在策略定义中配置并分配（绑定的范围内全部虚拟机生效）
      return this.$store.dispatch('scopedPolicy/get', {
        category: [MONITOR_TEMPLATE_VMINSTANCE],
      })
    },
    async fetchResource () {
      if (this.needFetchResource) {
        try {
          const { data } = await new this.$Manager('servers', 'v1').get({ id: this.data.id, params: { details: true } })
          this.server = data
        } catch (err) {
          throw err
        }
      } else {
        this.server = this.data
      }
    },
    async fetchGpuDevices () {
      if (!this.serverId) {
        this.gpuHasDevices = false
        return
      }
      try {
        const { data: { data = [] } } = await new this.$Manager('guestisolateddevices').list({
          params: {
            guest_id: this.serverId,
            show_baremetal_isolated_devices: true,
            details: true,
            limit: 0,
            scope: this.$store.getters.scope,
          },
        })
        this.gpuHasDevices = data.length > 0
      } catch (e) {
        this.gpuHasDevices = false
      }
    },
    handleTabChange (tab) {
      this.currentMonitorType = tab
      this.$nextTick(() => {
        this.$bus.$emit('VmMonitorTypeChange', tab)
      })
    },
  },
}
</script>
